// src/context/ProductsContext.jsx
import React, { createContext, useState, useEffect } from "react";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

export const ProductsContext = createContext();

// 🔹 Helper: fetch with 1.5s timeout
async function fetchWithTimeout(url, options = {}, timeout = 1500) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(id);
    return res;
  } catch (err) {
    clearTimeout(id);
    throw err;
  }
}

// 🔹 Notify backend of fallback (non-blocking)
async function notifyFallback(reason) {
  try {
    await fetch("https://firebase-cache-backend-dnat.onrender.com/fallback-log", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason }),
    });
  } catch (err) {
    console.warn("⚠️ Failed to notify backend about fallback:", err);
  }
}

export function ProductsProvider({ children }) {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    async function loadProducts() {
      try {
        window.__PRODUCTS_READY__ = false;
        // 1️⃣ Load cached products + timestamp from localStorage
        const cachedProducts = localStorage.getItem("products");
        const cachedTimestamp = localStorage.getItem("products_lastUpdated");

        if (cachedProducts) {
          setProducts(JSON.parse(cachedProducts));
          window.__PRODUCTS_READY__ = true;   // 👈 add this
        }

        let renderTimestamp = 0;

        // 2️⃣ Try Render API first (with timeout)
        try {
          const metaRes = await fetchWithTimeout(
            "https://firebase-cache-backend-dnat.onrender.com/meta"
          );
          if (!metaRes.ok) throw new Error("Meta API failed");
          const metaData = await metaRes.json();
          renderTimestamp = metaData.timestamp || 0;

          if (!cachedTimestamp || renderTimestamp > Number(cachedTimestamp)) {
            const prodRes = await fetchWithTimeout(
              "https://firebase-cache-backend-dnat.onrender.com/products"
            );
            if (!prodRes.ok) throw new Error("Products API failed");
            const renderProducts = await prodRes.json();

            setProducts(renderProducts);
            localStorage.setItem("products", JSON.stringify(renderProducts));
            localStorage.setItem("products_lastUpdated", renderTimestamp);
            window.__PRODUCTS_READY__ = true;   // 👈 add this
            return; // ✅ Done with Render
          }
        } catch (err) {
          console.warn("⚠️ Render API unavailable, falling back to Firebase:", err);
          notifyFallback("Render API failed or timed out");
        }

        // 3️⃣ Fallback → Firestore
        const metaSnap = await getDoc(doc(db, "metadata", "products"));
        const fbTimestamp = metaSnap.exists()
          ? metaSnap.data().lastUpdated?.toMillis() || 0
          : 0;

        if (!cachedTimestamp || fbTimestamp > Number(cachedTimestamp)) {
          const snapshot = await getDocs(collection(db, "products"));
          const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

          setProducts(list);
          localStorage.setItem("products", JSON.stringify(list));
          localStorage.setItem("products_lastUpdated", fbTimestamp);
          window.__PRODUCTS_READY__ = true;   // 👈 add this
        }
      } catch (err) {
        console.error("❌ Error loading products:", err);
        notifyFallback("Unexpected error in ProductsContext");
      }
    }

    loadProducts();
  }, []);

  return (
    <ProductsContext.Provider value={products}>
      {children}
    </ProductsContext.Provider>
  );
}
